import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getUserDetail, UserDetail, updateUserProfile, ProfileUpdateParams, resetUserPassword } from '../adminApi';
import { useAdmin } from '@/contexts/AdminContext';
import PasswordResetModal from '../components/admin/PasswordResetModal';

export default function AdminUserEditPage() {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated } = useAdmin();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [email, setEmail] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [fullName, setFullName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [gender, setGender] = useState('');
  const [bio, setBio] = useState('');

  // Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const toastRef = useRef<ReturnType<typeof setTimeout>>();
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(null), 3000);
  };

  // Password reset
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetData, setResetData] = useState<{
    temporaryPassword: string;
    expiresAt: string;
    emailSent: boolean;
    warning: string | null;
  } | null>(null);

  const fetchUser = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getUserDetail(id);
      setDetail(data);
      const user = data.user as Record<string, string>;
      setEmail(user.email || '');
      setIsVerified(Boolean(user.is_verified));
      setFullName(user.full_name || '');
      setBirthDate(user.birth_date || '');
      setBirthTime(user.birth_time || '');
      setBirthPlace(user.birth_place || '');
      setGender(user.gender || '');
      setBio(user.bio || '');
    } catch {
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchUser();
  }, [isAuthenticated, navigate, fetchUser]);

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const params: ProfileUpdateParams = {};
      if (email) params.email = email;
      params.is_verified = isVerified;
      if (fullName) params.full_name = fullName;
      if (birthDate) params.birth_date = birthDate;
      if (birthTime) params.birth_time = birthTime;
      if (birthPlace) params.birth_place = birthPlace;
      if (gender) params.gender = gender;
      if (bio) params.bio = bio;

      await updateUserProfile(id, params);
      showToast('Профиль обновлён');
      fetchUser();
    } catch (err) {
      showToast('Ошибка: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!id) return;
    try {
      const res = await resetUserPassword(id);
      setResetData(res.data);
      setResetModalOpen(true);
    } catch (err) {
      showToast('Ошибка: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'), 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white text-lg">Загрузка...</div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white text-lg">Пользователь не найден</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {toast && <div className={`fixed top-4 right-4 z-[60] px-4 py-3 rounded-lg text-sm font-medium shadow-lg ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>{toast.message}</div>}
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/admin/users" className="text-xl font-bold text-white hover:text-purple-400 transition">
            ← Назад
          </Link>
          <h1 className="text-xl font-bold text-white">
            {fullName || email || 'Редактирование'}
          </h1>
          <div className="flex gap-2">
            <button
              onClick={handleResetPassword}
              className="px-4 py-2 text-sm bg-orange-600/20 text-orange-400 rounded-lg hover:bg-orange-600/40 transition"
            >
              Сбросить пароль
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Account Section */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Аккаунт</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Верифицирован</label>
              <div className="flex items-center gap-3 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isVerified}
                    onChange={(e) => setIsVerified(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-purple-500 focus:ring-purple-500"
                  />
                  <span className="text-white text-sm">Да</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Natal Data Section */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Натальные данные</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Полное имя</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Дата рождения</label>
              <input
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Время рождения</label>
              <input
                type="time"
                value={birthTime}
                onChange={(e) => setBirthTime(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Место рождения</label>
              <input
                type="text"
                value={birthPlace}
                onChange={(e) => setBirthPlace(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Пол</label>
              <select
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Не указан</option>
                <option value="male">Мужской</option>
                <option value="female">Женский</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm text-slate-400 mb-1">Био</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              className="w-full px-4 py-2.5 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={() => navigate(`/admin/users/${id}`)}
            className="px-6 py-2.5 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
          >
            Отмена
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
          >
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </main>

      {/* Password Reset Modal */}
      {resetModalOpen && resetData && (
        <PasswordResetModal
          temporaryPassword={resetData.temporaryPassword}
          expiresAt={resetData.expiresAt}
          emailSent={resetData.emailSent}
          warning={resetData.warning}
          onClose={() => setResetModalOpen(false)}
        />
      )}
    </div>
  );
}
